import { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import {
  ClipboardList, Loader2, Truck, Search, FileVideo, FileAudio, Image as ImageIcon, Package
} from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const getToken = () => localStorage.getItem('token');
const getAuthHeader = () => ({ headers: { Authorization: `Bearer ${getToken()}` } });

const fmtDate = (d) => { try { return new Date(d + 'T00:00:00').toLocaleDateString(undefined, { weekday: 'short', year: 'numeric', month: 'short', day: 'numeric' }); } catch { return d; } };
const fmtTime = (iso) => { try { return new Date(iso).toLocaleString('en-GB', { timeZone: 'Africa/Lagos', day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit', hour12: true }); } catch { return ''; } };

export default function DistributorStockReports() {
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [singleDate, setSingleDate] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [textSearch, setTextSearch] = useState('');

  const srcFor = (att) => `${BACKEND_URL}${att.url}?auth=${getToken()}`;

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (singleDate) params.date = singleDate;
      else {
        if (startDate) params.start_date = startDate;
        if (endDate) params.end_date = endDate;
      }
      const res = await axios.get(`${API}/farm-operations-reports`, { ...getAuthHeader(), params });
      setReports(res.data || []);
    } catch (e) {
      setReports([]);
    } finally {
      setLoading(false);
    }
  }, [singleDate, startDate, endDate]);

  useEffect(() => { load(); }, [load]);

  // Mark activity as seen on open so the sidebar badge clears
  useEffect(() => {
    const markSeen = async () => {
      try {
        await axios.post(`${API}/farm-operations-reports-mark-seen`, {}, getAuthHeader());
        window.dispatchEvent(new Event('farmOpsSeen'));
      } catch (e) { /* ignore */ }
    };
    markSeen();
  }, []);

  const filtered = textSearch.trim()
    ? reports.filter((r) =>
        (r.text || '').toLowerCase().includes(textSearch.toLowerCase()) ||
        (r.sender_name || '').toLowerCase().includes(textSearch.toLowerCase()))
    : reports;

  return (
    <div className="space-y-6" data-testid="distributor-stock-reports-page">
      <div className="flex items-start gap-3">
        <div className="w-12 h-12 rounded-xl bg-amber-100 flex items-center justify-center shrink-0">
          <Package className="w-6 h-6 text-amber-600" />
        </div>
        <div>
          <h1 className="text-4xl font-bold tracking-tight text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>Available Stock</h1>
          <p className="text-sm text-slate-500">Reports shared with distributors — available egg and empty feed bag stock updates.</p>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-end gap-3 bg-white border rounded-xl p-3">
        <div>
          <Label className="text-xs text-slate-500">On date</Label>
          <Input type="date" value={singleDate} onChange={(e) => { setSingleDate(e.target.value); setStartDate(''); setEndDate(''); }} className="mt-1" data-testid="stock-filter-date" />
        </div>
        <span className="text-slate-300 pb-2">or</span>
        <div>
          <Label className="text-xs text-slate-500">From</Label>
          <Input type="date" value={startDate} onChange={(e) => { setStartDate(e.target.value); setSingleDate(''); }} className="mt-1" data-testid="stock-filter-from" />
        </div>
        <div>
          <Label className="text-xs text-slate-500">To</Label>
          <Input type="date" value={endDate} onChange={(e) => { setEndDate(e.target.value); setSingleDate(''); }} className="mt-1" data-testid="stock-filter-to" />
        </div>
        <div className="flex-1 min-w-[180px]">
          <Label className="text-xs text-slate-500">Search</Label>
          <div className="relative mt-1">
            <Search className="w-4 h-4 text-slate-400 absolute left-2 top-1/2 -translate-y-1/2" />
            <Input value={textSearch} onChange={(e) => setTextSearch(e.target.value)} placeholder="Search text or sender" className="pl-8" data-testid="stock-filter-search" />
          </div>
        </div>
      </div>

      {/* List */}
      {loading ? (
        <div className="flex items-center justify-center py-16 text-slate-400"><Loader2 className="w-6 h-6 animate-spin mr-2" /> Loading reports...</div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-16 text-slate-400" data-testid="no-stock-reports-msg">
          <ClipboardList className="w-10 h-10 mx-auto mb-2 opacity-40" />
          No stock reports available.
        </div>
      ) : (
        <div className="space-y-4">
          {filtered.map((r) => (
            <div key={r.id} className="bg-white border rounded-xl p-4 shadow-sm" data-testid={`stock-report-card-${r.id}`}>
              <div className="flex items-center gap-2 flex-wrap">
                <span className="font-semibold text-slate-800">{r.sender_name}</span>
                <span className="text-xs text-slate-400">· {fmtDate(r.date)}</span>
                <Badge className="bg-amber-100 text-amber-700 hover:bg-amber-100"><Truck className="w-3 h-3 mr-1" /> Management & Distributors</Badge>
              </div>

              {r.text && <p className="text-sm text-slate-700 mt-2 whitespace-pre-wrap">{r.text}</p>}

              {r.attachments && r.attachments.length > 0 && (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-3">
                  {r.attachments.map((att, i) => (
                    <div key={i} className="border rounded-lg p-2 bg-slate-50" data-testid={`stock-attachment-${r.id}-${i}`}>
                      {att.type === 'video' && (
                        <video src={srcFor(att)} controls className="w-full rounded max-h-64" preload="metadata" />
                      )}
                      {att.type === 'audio' && (
                        <audio src={srcFor(att)} controls className="w-full" preload="metadata" />
                      )}
                      {att.type === 'image' && (
                        <a href={srcFor(att)} target="_blank" rel="noreferrer">
                          <img src={srcFor(att)} alt={att.filename} className="w-full rounded max-h-64 object-cover" />
                        </a>
                      )}
                      <div className="flex items-center gap-1 text-xs text-slate-500 mt-1 truncate">
                        {att.type === 'video' && <FileVideo className="w-3 h-3" />}
                        {att.type === 'audio' && <FileAudio className="w-3 h-3" />}
                        {att.type === 'image' && <ImageIcon className="w-3 h-3" />}
                        <span className="truncate">{att.filename}</span>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {r.responses && r.responses.length > 0 && (
                <div className="mt-3 space-y-2 border-l-2 border-slate-100 pl-3">
                  {r.responses.map((resp) => {
                    const mgmt = ['admin', 'manager'].includes(resp.author_role);
                    return (
                      <div key={resp.id} className={`rounded-lg p-2 text-sm ${mgmt ? 'bg-emerald-50' : 'bg-slate-50'}`}>
                        <div className="flex items-center gap-2 text-xs text-slate-500 mb-0.5">
                          <span className="font-medium text-slate-700">{resp.author_name}</span>
                          {mgmt && <Badge className="bg-emerald-600 text-white hover:bg-emerald-600 text-[10px] px-1 py-0">Management</Badge>}
                          <span>· {fmtTime(resp.created_at)}</span>
                        </div>
                        <p className="text-slate-700 whitespace-pre-wrap">{resp.text}</p>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
